import ExcelJS from 'exceljs';
import { normalizeCell } from './normalizeCell';

export function notIn<T>(set: Set<T>) {
  return (v: T) => !set.has(v);
}

export async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
}

export async function extractJsonDataFromFile(
  file: File
): Promise<{ rows: string[][]; sheetName: string }> {
  const data = await readFileAsArrayBuffer(file);
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(data);
  const ws = workbook.worksheets[0];

  const rows: string[][] = [];
  for (let r = 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const values = Array.isArray(row.values) ? row.values : [];
    const rowValues: string[] = [];
    for (let i = 1; i < values.length; i++) {
      rowValues.push(normalizeCell(values[i]));
    }
    rows.push(rowValues);
  }

  return { rows, sheetName: ws.name || 'Sheet1' };
}

export function normalizeHeader(header: unknown): string {
  const text = String(header).trim();
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export async function convertHeadersToTitleCase(file: File): Promise<File> {
  try {
    const { rows, sheetName } = await extractJsonDataFromFile(file);
    if (rows.length === 0) return file;

    rows[0] = rows[0].map(normalizeHeader);

    const newWorkbook = new ExcelJS.Workbook();
    const newWorksheet = newWorkbook.addWorksheet(sheetName);

    if (typeof (newWorksheet as any).addRows === 'function') {
      (newWorksheet as any).addRows(rows);
    } else {
      for (const row of rows) newWorksheet.addRow(row);
    }

    const buffer = await newWorkbook.xlsx.writeBuffer();
    return new File([buffer], file.name, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  } catch {
    return file;
  }
}

export async function validateExcelHeaders(
  file: File,
  requiredFields: string[],
  showAlert: (a: { text: string; message: string; status: 'error' | 'success' | 'info' | 'warning' }) => void
): Promise<boolean> {
  try {
    const { rows } = await extractJsonDataFromFile(file);

    if (rows.length === 0) {
      showAlert({ text: 'Archivo vacío', message: 'El archivo no contiene datos.', status: 'error' });
      return false;
    }

    const headers = rows[0] || [];
    const headersSet = new Set(headers);
    const missing = requiredFields.filter(notIn(headersSet));

    if (missing.length > 0) {
      showAlert({
        text: 'Campos faltantes en el archivo',
        message: `Los siguientes campos son requeridos: ${missing.join(', ')}`,
        status: 'error',
      });
      return false;
    }

    return true;
  } catch (error: unknown) {
    const message =
      error instanceof Error
        ? error.message
        : 'No se pudo procesar el archivo. Verifique que sea un archivo Excel válido.';
    showAlert({ text: 'Error al leer el archivo', message, status: 'error' });
    return false;
  }
}
